import type { TZDate } from "@date-fns/tz";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import type { View } from "react-native";

import SnapPickerItem from "$src/core/components/atoms/SnapPickerItem/SnapPickerItem";
import Text from "$src/core/components/atoms/Text/Text";
import HorizontalSnapScrollView from "$src/core/components/molecules/HorizontalSnapScrollView/HorizontalSnapScrollView";
import { TV_PARALLAX } from "$src/core/constants/tv-parallax";
import useHorizontalSnapPickerMetrics from "$src/core/hooks/use-horizontal-snap-picker-metrics";
import useTranslation from "$src/core/hooks/use-translation";
import buildRelativeDateRange from "$src/core/utils/date/build-relative-date-range";
import isIPad from "$src/core/utils/device/is-ipad";
import CustomPlatform from "$src/core/utils/platform/platform";

import $ from "./DatePicker.styles";

type DatePickerProps = {
  daysBefore: number;
  daysAfter: number;
  timeZone: string;
  selectedOffset: number;
  onDateSelect?: (offset: number) => void;
  selectedItemRef?: React.Ref<View | null>;
  onItemFocus?: () => void;
};

const LABEL_PRESET = CustomPlatform.select<"tiny" | "heading6">({
  tv: "tiny",
  default: "heading6",
});

/** Only the phone is too narrow to size the date pills by width, so it shows a fixed 3. */
const VISIBLE_DATE_COUNT = CustomPlatform.select<number | undefined>({
  native: isIPad ? undefined : 3,
});

const DatePicker = ({
  daysAfter,
  daysBefore,
  timeZone,
  selectedOffset,
  onDateSelect,
  selectedItemRef,
  onItemFocus,
}: DatePickerProps) => {
  const { t, d } = useTranslation();

  const { scrollViewRef, onLayout, itemWidth, snapToInterval, centerItem } =
    useHorizontalSnapPickerMetrics({
      visibleItemCount: VISIBLE_DATE_COUNT,
    });

  const dates = useMemo(
    () =>
      buildRelativeDateRange({
        daysBefore,
        daysAfter,
        timeZone,
      }),
    [daysBefore, daysAfter, timeZone],
  );

  const handleScrollToDate = useCallback(
    (offset: number) => {
      centerItem(
        dates.findIndex((item) => item.offset === offset),
        dates.length,
        CustomPlatform.select({
          tvOS: false,
          default: true,
        }),
      );
    },
    [centerItem, dates],
  );

  const handleOnDateSelect = (offset: number) => {
    handleScrollToDate(offset);
    onDateSelect?.(offset);
  };

  const setRef = useCallback(
    (targetRef: React.Ref<View | null> | undefined, value: View | null) => {
      if (typeof targetRef === "function") {
        targetRef(value);
      } else if (targetRef) {
        // eslint-disable-next-line no-param-reassign
        targetRef.current = value;
      }
    },
    [],
  );

  const itemNodes = useRef<Map<number, View>>(new Map());

  /* Republish the selected pill: a child ref fires on mount only, not when the selection moves. */
  useEffect(() => {
    setRef(selectedItemRef, itemNodes.current.get(selectedOffset) ?? null);
  }, [dates, selectedItemRef, selectedOffset, setRef]);

  /* Keep the selected day centred: on mount, once measured, and on every change. */
  useEffect(() => {
    handleScrollToDate(selectedOffset);
  }, [handleScrollToDate, selectedOffset]);

  const formatDateLabel = (offset: number, date: TZDate) => {
    switch (offset) {
      case -1:
        return t("DATE/YESTERDAY");
      case 0:
        return t("DATE/TODAY");
      case 1:
        return t("DATE/TOMORROW");
      default:
        return d(date.toISOString(), "short-weekday-day-month");
    }
  };

  return (
    <HorizontalSnapScrollView
      ref={scrollViewRef}
      onLayout={onLayout}
      snapToInterval={snapToInterval}
    >
      {dates.map((item) => {
        const isActive = selectedOffset === item.offset;
        const label = formatDateLabel(item.offset, item.date);

        return (
          <SnapPickerItem
            key={`${item.offset}-${item.date.getTime()}`}
            ref={(node: View | null) => {
              if (node) itemNodes.current.set(item.offset, node);
              else itemNodes.current.delete(item.offset);
            }}
            width={itemWidth}
            isActive={isActive}
            accessibilityLabel={label}
            accessibilityState={{ selected: isActive }}
            onItemFocus={onItemFocus}
            onPress={() => handleOnDateSelect(item.offset)}
            tvParallaxProperties={CustomPlatform.select({
              tvOS: TV_PARALLAX.none,
              default: undefined,
            })}
          >
            <Text
              style={[$.label, isActive && $.labelActive]}
              preset={LABEL_PRESET}
              ellipsizeMode="tail"
              numberOfLines={2}
              accessibilityRole="text"
            >
              {label}
            </Text>
          </SnapPickerItem>
        );
      })}
    </HorizontalSnapScrollView>
  );
};

export default DatePicker;
